

import { Direction, Directionality } from '@angular/cdk/bidi';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Optional,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewEncapsulation
} from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { slideAlertMotion } from 'mwc/core/animation';
import { MwcConfigKey, MwcConfigService, WithConfig } from 'mwc/core/config';
import { BooleanInput } from 'mwc/core/types';
import { InputBoolean } from 'mwc/core/util';

const NZ_CONFIG_MODULE_NAME: MwcConfigKey = 'alert';

@Component({
  selector: 'mwc-alert',
  exportAs: 'mwcAlert',
  animations: [slideAlertMotion],
  template: `
    <div
      *ngIf="!closed"
      class="mwc-alert"
      [class.mwc-alert-rtl]="dir === 'rtl'"
      [class.mwc-alert-success]="mwcType === 'success'"
      [class.mwc-alert-info]="mwcType === 'info'"
      [class.mwc-alert-warning]="mwcType === 'warning'"
      [class.mwc-alert-error]="mwcType === 'error'"
      [class.mwc-alert-default]="mwcType === 'default'"
      [class.mwc-alert-no-icon]="!mwcShowIcon"
      [class.mwc-alert-banner]="mwcBanner"
      [class.mwc-alert-closable]="mwcCloseable"
      [class.mwc-alert-with-description]="!!mwcDescription"
      [@.disabled]="mwcNoAnimation"
      [@slideAlertMotion]
      (@slideAlertMotion.done)="onFadeAnimationDone()"
    >
      <div *ngIf="mwcShowIcon" class="mwc-alert-icon">
        <ng-container *ngIf="mwcIcon; else iconDefaultTemplate">
          <ng-container *mwcStringTemplateOutlet="mwcIcon"></ng-container>
        </ng-container>
        <ng-template #iconDefaultTemplate>
          <span mwc-icon [mwcIconfont]="mwcIconType || inferredIconType"></span>
        </ng-template>
      </div>
      <div class="mwc-alert-content" *ngIf="mwcMessage || mwcDescription">
        <span class="mwc-alert-message" *ngIf="mwcMessage">
          <ng-container *mwcStringTemplateOutlet="mwcMessage">{{ mwcMessage }}</ng-container>
        </span>
        <span class="mwc-alert-description" *ngIf="mwcDescription">
          <ng-container *mwcStringTemplateOutlet="mwcDescription">{{ mwcDescription }}</ng-container>
        </span>
        <!-- <span>
        <a mwc-button mwcSize="small" class="mwc-alert-description-actionBtn-secondary" mwcType="link">Learn more</a>
            <a mwc-button mwcSize="small" class="mwc-alert-description-actionBtn-primary" mwcType="link">View changes</a>
            <span mwc-icon mwcIconfont="arrow-right" class="mwc-alert-arrow-right-icon"></span>
        </span> -->
      </div>
      <span mwc-icon mwcIconfont="x-close" class="mwc-alert-closebtn" *ngIf="mwcCloseable || mwcCloseText" (click)="closeAlert()" ></span>
      <div class="mwc-alert-action" *ngIf="mwcAction">
        <ng-container *mwcStringTemplateOutlet="mwcAction">{{ mwcAction }}</ng-container>
      </div>
      <!-- <button
        type="button"
        tabindex="0"
        *ngIf="mwcCloseable || mwcCloseText"
        class="mwc-alert-close-icon"
        (click)="closeAlert()"
      > -->
        <!-- <ng-template #closeDefaultTemplate>
          <span mwc-icon mwcType="close"></span>
        </ng-template>
        <ng-container *ngIf="mwcCloseText; else closeDefaultTemplate">
          <ng-container *mwcStringTemplateOutlet="mwcCloseText">
            <span class="mwc-alert-close-text">{{ mwcCloseText }}</span>
          </ng-container>
        </ng-container>
      </button> -->
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  preserveWhitespaces: false
})
export class MwcAlertComponent implements OnChanges, OnDestroy, OnInit {
  readonly _mwcModuleName: MwcConfigKey = NZ_CONFIG_MODULE_NAME;
  static ngAcceptInputType_mwcCloseable: BooleanInput;
  static ngAcceptInputType_mwcShowIcon: BooleanInput;
  static ngAcceptInputType_mwcBanner: BooleanInput;
  static ngAcceptInputType_mwcNoAnimation: BooleanInput;

  @Input() mwcAction: string | TemplateRef<void> | null = null;
  @Input() mwcCloseText: string | TemplateRef<void> | null = null;
  @Input() mwcIconType: string | null = null;
  @Input() mwcMessage: string | TemplateRef<void> | null = null;
  @Input() mwcDescription: string | TemplateRef<void> | null = null;
  @Input() mwcType: 'success' | 'info' | 'warning' | 'error' | 'default' = 'info';
  @Input() @WithConfig() @InputBoolean() mwcCloseable: boolean = false;
  @Input() @WithConfig() @InputBoolean() mwcShowIcon: boolean = false;
  @Input() @InputBoolean() mwcBanner = false;
  @Input() @InputBoolean() mwcNoAnimation = false;
  @Input() mwcIcon: string | TemplateRef<void> | null = null;
  @Output() readonly mwcOnClose = new EventEmitter<boolean>();
  closed = false;
  iconTheme: 'outline' | 'fill' = 'fill';
  inferredIconType: string = 'info-circle';
  dir: Direction = 'ltr';
  private isTypeSet = false;
  private isShowIconSet = false;
  private destroy$ = new Subject();

  constructor(
    public mwcConfigService: MwcConfigService,
    private cdr: ChangeDetectorRef,
    @Optional() private directionality: Directionality
  ) {
    this.mwcConfigService
      .getConfigChangeEventForComponent(NZ_CONFIG_MODULE_NAME)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.cdr.markForCheck();
      });
  }

  ngOnInit(): void {
    this.directionality.change?.pipe(takeUntil(this.destroy$)).subscribe((direction: Direction) => {
      this.dir = direction;
      this.cdr.detectChanges();
    });

    this.dir = this.directionality.value;
  }

  closeAlert(): void {
    this.closed = true;
  }

  onFadeAnimationDone(): void {
    if (this.closed) {
      this.mwcOnClose.emit(true);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    const { mwcShowIcon, mwcDescription, mwcType, mwcBanner } = changes;
    if (mwcShowIcon) {
      this.isShowIconSet = true;
    }
    if (mwcType) {
      this.isTypeSet = true;
      switch (this.mwcType) {
        case 'error':
          this.inferredIconType = 'alert-circle';
          break;
        case 'success':
          this.inferredIconType = 'check-circle';
          break;
        case 'info':
          this.inferredIconType = 'info-circle';
          break;
        case 'default':
          this.inferredIconType = 'info-circle';
            break;
        case 'warning':
          this.inferredIconType = 'alert-triangle';
          break;
      }
    }
    if (mwcDescription) {
      this.iconTheme = this.mwcDescription ? 'outline' : 'fill';
    }
    if (mwcBanner) {
      if (!this.isTypeSet) {
        this.mwcType = 'warning';
      }
      if (!this.isShowIconSet) {
        this.mwcShowIcon = true;
      }
    }
  }
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
